import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Badge, Drawer, ErrorBanner, Spinner } from '@saas-kit/ui';

import {
  getAnnouncement,
  getDeliverySummary,
  type Announcement,
} from './api';

interface Props {
  announcement: Announcement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

export function AnnouncementSummaryDrawer({
  announcement,
  open,
  onOpenChange,
}: Props): JSX.Element {
  const { t } = useTranslation('adminAnnouncements');

  const detailQuery = useQuery({
    queryKey: ['admin-announcement', announcement.id],
    queryFn: () => getAnnouncement(announcement.id),
    enabled: open,
  });
  const summaryQuery = useQuery({
    queryKey: ['admin-announcement-deliveries', announcement.id],
    queryFn: () => getDeliverySummary(announcement.id),
    enabled: open,
  });

  const current = detailQuery.data ?? announcement;
  const summary = summaryQuery.data;

  const stats: { key: string; value: number }[] = summary
    ? [
        { key: 'total', value: summary.total },
        { key: 'sent', value: summary.sent },
        { key: 'failed', value: summary.failed },
        { key: 'pending', value: summary.pending },
        { key: 'skipped', value: summary.skipped },
      ]
    : [];

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={current.subject}
      description={t('summary.description')}
      testId="admin-announcement-summary"
      width="lg"
    >
      {detailQuery.isError ? (
        <ErrorBanner>{t('errors.load_failed')}</ErrorBanner>
      ) : null}

      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">
            {t(`status.${current.status}`, { defaultValue: current.status })}
          </Badge>
          <Badge variant="outline">
            {t(`audience.${current.audience_kind}`)}
          </Badge>
          {current.channels.map((channel) => (
            <Badge key={channel} variant="outline">
              {t(`channels.${channel}`)}
            </Badge>
          ))}
        </div>

        <div className="whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
          {current.body}
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">{t('summary.published_at')}</dt>
          <dd>{formatDate(current.published_at)}</dd>
          <dt className="text-muted-foreground">{t('summary.created_at')}</dt>
          <dd>{formatDate(current.created_at)}</dd>
        </dl>

        <section>
          <h3 className="mb-2 text-sm font-medium text-foreground">
            {t('summary.deliveries')}
          </h3>
          {summaryQuery.isError ? (
            <ErrorBanner>{t('summary.deliveries_failed')}</ErrorBanner>
          ) : summaryQuery.isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner size="sm" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {stats.map((stat) => (
                <div
                  key={stat.key}
                  className="rounded-md border border-border bg-card p-3 text-center"
                >
                  <p className="text-lg font-semibold text-foreground">
                    {stat.value}
                  </p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t(`summary.stats.${stat.key}`)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        {current.audience_kind === 'specific' &&
        current.audience_tenant_ids &&
        current.audience_tenant_ids.length > 0 ? (
          <section>
            <h3 className="mb-2 text-sm font-medium text-foreground">
              {t('summary.audience_tenants')}
            </h3>
            <ul className="space-y-1">
              {current.audience_tenant_ids.map((id) => (
                <li key={id} className="font-mono text-xs text-muted-foreground">
                  {id}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </Drawer>
  );
}
